import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { createLocalizedUrl } from '../utils/languageUtils';
import { IconLanguageSelector } from './LanguageSelector';
import { ArrowLeftIcon, Bars3Icon, XMarkIcon, ChevronDownIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { loadHubsConfigForLocale } from '../config/configRegistry';
import { api, endpoints } from '../config/api';

/**
 * Shared site header component used across all public pages
 * Provides consistent navigation, language switching, and authentication UI
 */
const SiteHeader = ({ 
    showBackButton = false, 
    backLink = "/",
    backText = null,
    showAuthButtons = true,
    className = ""
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { currentLanguage, changeLanguage } = useLanguage();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [guidesDropdownOpen, setGuidesDropdownOpen] = useState(false);
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [hubsConfig, setHubsConfig] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const userDropdownRef = React.useRef(null);
    const desktopAccountButtonRef = React.useRef(null);
    const mobileAccountButtonRef = React.useRef(null);

    // Load hubs config for guides dropdown
    useEffect(() => {
        (async () => {
            try {
                const cfg = await loadHubsConfigForLocale(currentLanguage);
                setHubsConfig(cfg);
            } catch (e) {
                setHubsConfig(null);
            }
        })();
    }, [currentLanguage]);

    // Close user dropdown on outside click ONLY for touch/coarse pointers (mobile/tablet).
    // Desktop closes on hover-out, so we avoid double-handling.
    useEffect(() => {
        const isCoarsePointer = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
        if (!isCoarsePointer) return;

        const handleClickOutside = (event) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
                setUserDropdownOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => document.removeEventListener('click', handleClickOutside, true);
    }, [userDropdownOpen]);

    // Check authentication status
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const token = localStorage.getItem('authToken');
                if (!token) {
                    setIsAuthenticated(false);
                    setUser(null);
                    setAuthLoading(false);
                    return;
                }

                // Verify token and fetch user profile from backend
                const response = await api.get(endpoints.profile.get);
                if (response.data.success && response.data.profile) {
                    setIsAuthenticated(true);
                    setUser(response.data.profile);
                } else {
                    setIsAuthenticated(false);
                    setUser(null);
                }
            } catch (error) {
                // Silently handle auth check errors (401 is expected for non-authenticated users)
                if (error.response?.status !== 401) {
                    console.error('Auth check error:', error);
                }
                setIsAuthenticated(false);
                setUser(null);
            } finally {
                setAuthLoading(false);
            }
        };

        checkAuth();
        
        // Listen for auth changes (when user logs in/out in another tab)
        const handleStorageChange = (e) => {
            if (e.key === 'authToken') {
                checkAuth();
            }
        };
        window.addEventListener('storage', handleStorageChange);
        
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
        setIsAuthenticated(false);
        setUser(null);
        setUserDropdownOpen(false);
        setAuthLoading(false);
        navigate('/');
    };

    const handleLanguageChange = async (nextLang) => {
        const oneYear = 60*60*24*365;
        const isSecure = window.location.protocol === 'https:';
        document.cookie = `preferred_locale=${nextLang};path=/;max-age=${oneYear};SameSite=Lax${isSecure ? ';Secure' : ''}`;
        try { localStorage.setItem('preferred_locale', nextLang); } catch {}
        changeLanguage(nextLang);
        const basePath = window.location.pathname.replace(/^\/(en|es|fr)/, '') || '/';
        const localizedPath = createLocalizedUrl(basePath, nextLang);
        navigate(localizedPath, { replace: true });
        setMobileMenuOpen(false);
    };

    const defaultBackText = backText || t('common.backToHome', 'Back to home');

    return (
        <header className={`w-full bg-white border-b border-gray-100 shadow-sm ${className}`}>
            {/* Skip link for keyboard users */}
            <a href="#main" className="skipnav btn btn--sm sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[1000]">Skip to content</a>
            <div className="container mx-auto px-4 py-4 relative">
                <nav className="grid h-[var(--site-header-height)] grid-cols-[1fr_auto_auto] lg:grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2">
                    {/* Left (logo) */}
                    <Link to="/" aria-label="Homepage" className="col-start-1 col-end-2 flex items-center gap-1 md:gap-2 hover:opacity-90 transition-opacity flex-shrink-0 lg:pr-6 lg:border-r lg:border-gray-100">
                        <img
                            src="/assets/LiveLoveSpain-64.webp"
                            srcSet="/assets/LiveLoveSpain-32.webp 32w, /assets/LiveLoveSpain-64.webp 64w, /assets/LiveLoveSpain-128.webp 128w"
                            sizes="64px"
                            width="64"
                            height="64"
                            alt="LiveLoveSpain Logo"
                            className="h-8 md:h-10 w-auto"
                            loading="eager"
                        />
                        <span className="hidden xl:inline ml-1 text-base md:text-md font-bold tracking-tight text-blue-600">LiveLoveSpain</span>
                    </Link>

                    {/* Center (desktop nav) - centered within middle column */}
                    <div className="hidden lg:flex items-center justify-center mx-2 xl:mx-6 lg:col-start-2 lg:col-end-3 min-w-0 overflow-visible">
                        <div className="flex items-center flex-nowrap space-x-3 xl:space-x-5 2xl:space-x-7">
                            <a 
                                href={createLocalizedUrl("/#demo", currentLanguage)} 
                                className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 font-medium xl:font-semibold text-sm xl:text-base whitespace-nowrap px-3 py-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            >
                                Demo
                            </a>
                            <a 
                                href={createLocalizedUrl("/#pricing", currentLanguage)} 
                                className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 font-medium xl:font-semibold text-sm xl:text-base whitespace-nowrap px-3 py-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            >
                                {t('header.pricing')}
                            </a>
                            <Link 
                                to={createLocalizedUrl("/cost-explorer", currentLanguage)} 
                                className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 font-medium xl:font-semibold text-sm xl:text-base whitespace-nowrap px-3 py-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            >
                                {t('welcome.costExplorer')}
                            </Link>
                            <div className="relative" onMouseLeave={() => setGuidesDropdownOpen(false)}>
                                <button 
                                    onClick={() => setGuidesDropdownOpen(!guidesDropdownOpen)}
                                    onMouseEnter={() => setGuidesDropdownOpen(true)}
                                    className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 font-medium xl:font-semibold text-sm xl:text-base whitespace-nowrap px-3 py-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 flex items-center gap-1"
                                    aria-expanded={guidesDropdownOpen}
                                    aria-haspopup="true"
                                >
                                    {t('welcome.guides')}
                                    <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                                </button>
                                {guidesDropdownOpen && (
                                    <div className="absolute top-full left-0 pt-2 w-64 z-50">
                                        <div className="bg-white rounded-lg shadow-lg border border-gray-200">
                                        <div className="py-2">
                                            {hubsConfig?.hubs?.map((hub) => (
                                                <Link
                                                    key={hub.slug}
                                                    to={createLocalizedUrl(`/${hub.slug}`, currentLanguage)}
                                                    className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                                                    onClick={() => setGuidesDropdownOpen(false)}
                                                >
                                                    <div className="font-medium text-sm xl:text-base">{hub.shortTitle}</div>
                                                    <div className="text-xs xl:text-sm text-gray-500">{hub.tagline}</div>
                                                </Link>
                                            ))}
                                            <div className="border-t border-gray-200 mt-2 pt-2">
                                                <Link
                                                    to={createLocalizedUrl("/guides", currentLanguage)}
                                                    className="block px-4 py-3 text-blue-600 hover:bg-blue-50 font-medium text-sm xl:text-base"
                                                    onClick={() => setGuidesDropdownOpen(false)}
                                                >
                                                    {t('welcome.viewAllGuides')}
                                                </Link>
                                            </div>
                                        </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <Link 
                                to={createLocalizedUrl("/tools", currentLanguage)} 
                                className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 font-medium xl:font-semibold text-sm xl:text-base whitespace-nowrap px-3 py-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            >
                                {t('welcome.tools')}
                            </Link>
                        </div>
                    </div>

                    {/* Right (actions): languages + CTAs/Account */}
                    <div className="hidden lg:flex items-center gap-2 xl:gap-3 justify-self-end lg:pl-6 lg:border-l lg:border-gray-100 flex-nowrap whitespace-nowrap flex-shrink-0">
                        <IconLanguageSelector openOnHover={true} />
                        
                        {showAuthButtons && (
                            authLoading ? (
                                // Loading state - show skeleton or spinner
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                                    <div className="hidden md:block w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            ) : isAuthenticated ? (
                                // Logged in user dropdown
                                <div
                                    className="relative"
                                    ref={userDropdownRef}
                                    onMouseEnter={() => setUserDropdownOpen(true)}
                                    onMouseLeave={() => setUserDropdownOpen(false)}
                                >
                                    <button
                                        onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                                        className="btn btn--sm btn--ghost flex items-center gap-2 px-g2 xl:px-4 2xl:px-5 py-2 lg:h-9 lg:px-3.5 lg:py-1 lg:min-h-0 leading-none lg:text-[13px] text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 whitespace-nowrap"
                                        aria-expanded={userDropdownOpen}
                                        aria-haspopup="true"
                                        ref={desktopAccountButtonRef}
                                    >
                                        <UserCircleIcon className="block h-4 w-4 shrink-0" />
                                        <span className="text-xs xl:text-sm font-medium leading-none">{t('header.account')}</span>
                                        <ChevronDownIcon className="block h-4 w-4 shrink-0" aria-hidden="true" />
                                    </button>

                                    {userDropdownOpen && (
                                        <div
                                            className="absolute right-0 top-full mt-0 w-max bg-white shadow-xl rounded-lg py-2 border border-gray-100 z-[100]"
                                            style={{ minWidth: desktopAccountButtonRef.current ? desktopAccountButtonRef.current.offsetWidth : undefined }}
                                        >
                                            <Link
                                                to="/task-manager"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                                onClick={(e) => { e.stopPropagation(); setUserDropdownOpen(false); }}
                                            >
                                                {t('header.dashboard', 'Dashboard')}
                                            </Link>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleLogout(); }}
                                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                            >
                                                {t('header.logout', 'Logout')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                // Not logged in - show login/signup buttons
                                <>
                                    <Link 
                                        to="/login" 
                                        className="btn btn--sm btn--ghost px-g2 xl:px-4 2xl:px-5 py-2 lg:h-9 lg:px-3.5 lg:py-1 lg:min-h-0 lg:leading-tight lg:text-[13px] text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all font-medium text-xs xl:text-sm flex items-center gap-1.5 xl:gap-2 shadow-sm whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                    >
                                        <svg className="w-4 h-4 lg:w-3.5 lg:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        {t('header.login')}
                                    </Link>
                                    <Link 
                                        to="/login" 
                                        className="btn btn--sm px-g2 xl:px-4 2xl:px-5 py-2 lg:h-9 lg:px-3.5 lg:py-1 lg:min-h-0 lg:leading-tight lg:text-[13px] bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 rounded-lg hover:from-yellow-500 hover:to-yellow-600 transition-all font-medium xl:font-semibold text-xs xl:text-sm flex items-center gap-1.5 xl:gap-2 shadow-sm whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                    >
                                        {t('header.getStarted')}
                                        <svg className="w-4 h-4 lg:w-3.5 lg:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                        </svg>
                                    </Link>
                                </>
                            )
                        )}
                    </div>

                    {/* Mobile CTAs on top bar (visible until lg) */}
                    <div className="block lg:hidden justify-self-end">
                        {showAuthButtons && (
                            authLoading ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 bg-gray-200 rounded-full animate-pulse"></div>
                                    <div className="w-14 h-4 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            ) : isAuthenticated ? (
                                <div className="relative inline-block" ref={userDropdownRef}>
                                    <button
                                        onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                                        className="px-3 py-2 text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 inline-flex items-center gap-1.5 leading-none whitespace-nowrap"
                                        aria-expanded={userDropdownOpen}
                                        aria-haspopup="true"
                                        ref={mobileAccountButtonRef}
                                    >
                                        <UserCircleIcon className="block h-4 w-4 shrink-0" />
                                        <span className="leading-none">{t('header.account', 'Account')}</span>
                                        <ChevronDownIcon className="block h-4 w-4 shrink-0" aria-hidden="true" />
                                    </button>
                                    {userDropdownOpen && (
                                        <div
                                            className="absolute right-0 top-full mt-0 w-max bg-white shadow-xl rounded-lg py-2 border border-gray-100 z-[100]"
                                            style={{ minWidth: mobileAccountButtonRef.current ? mobileAccountButtonRef.current.offsetWidth : undefined }}
                                        >
                                            <Link
                                                to="/task-manager"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                                onClick={() => setUserDropdownOpen(false)}
                                            >
                                                {t('header.dashboard', 'Dashboard')}
                                            </Link>
                                            <button
                                                onClick={() => {
                                                    handleLogout();
                                                    setUserDropdownOpen(false);
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                            >
                                                {t('header.logout', 'Logout')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <Link 
                                        to="/login" 
                                        className="px-3 py-2 text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all text-xs font-medium mr-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                    >
                                        {t('header.login')}
                                    </Link>
                                    <Link 
                                        to="/login" 
                                        className="px-3 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 rounded-lg hover:from-yellow-500 hover:to-yellow-600 transition-all text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                    >
                                        {t('header.getStarted')}
                                    </Link>
                                </>
                            )
                        )}
                    </div>

                    {/* Mobile Menu Button (visible until lg) */}
                    <button 
                        className="block lg:hidden p-2 rounded-lg hover:bg-gray-100 justify-self-end"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle menu"
                        aria-expanded={mobileMenuOpen}
                    >
                        {mobileMenuOpen ? 
                            <XMarkIcon className="h-6 w-6" /> : 
                            <Bars3Icon className="h-6 w-6" />
                        }
                    </button>
                </nav>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="lg:hidden py-4 border-t border-gray-100 mt-4">
                        <div className="flex flex-col text-sm space-y-4">
                            {showBackButton && (
                                <Link 
                                    to={backLink}
                                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition font-medium pb-4 border-b border-gray-200"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <ArrowLeftIcon className="h-4 w-4" />
                                    {defaultBackText}
                                </Link>
                            )}
                            <a 
                                href={createLocalizedUrl("/#demo", currentLanguage)} 
                                className="text-gray-600 hover:text-blue-600 transition font-medium"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Demo
                            </a>
                            <a 
                                href={createLocalizedUrl("/#pricing", currentLanguage)} 
                                className="text-gray-600 hover:text-blue-600 transition font-medium"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {t('header.pricing')}
                            </a>
                            <Link 
                                to={createLocalizedUrl("/cost-explorer", currentLanguage)} 
                                className="text-gray-600 hover:text-blue-600 transition font-medium"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {t('welcome.costExplorer')}
                            </Link>
                            
                            <details className="group">
                                <summary className="list-none flex items-center justify-between cursor-pointer select-none text-gray-600 font-medium">
                                    <span>{t('welcome.guides')}</span>
                                    <ChevronDownIcon className="h-4 w-4 transition-transform group-open:rotate-180" />
                                </summary>
                                <div className="mt-2 space-y-1 pl-4">
                                    {hubsConfig?.hubs?.map((hub) => (
                                        <Link
                                            key={hub.slug}
                                            to={createLocalizedUrl(`/${hub.slug}`, currentLanguage)}
                                            className="block py-1 text-gray-600 hover:text-blue-600 transition"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            <div className="font-medium text-sm">{hub.shortTitle}</div>
                                            <div className="text-xs text-gray-500">{hub.tagline}</div>
                                        </Link>
                                    ))}
                                    <Link
                                        to="/guides"
                                        className="block py-1 text-blue-600 hover:text-blue-700 transition font-medium text-sm"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        {t('welcome.viewAllGuides')}
                                    </Link>
                                </div>
                            </details>

                            <Link 
                                to={createLocalizedUrl("/tools", currentLanguage)} 
                                className="text-gray-600 hover:text-blue-600 transition font-medium"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {t('welcome.tools')}
                            </Link>

                            {/* Auth options moved to top bar on mobile */}

                            {/* Language Selector */}
                            <div className="pt-4 border-t border-gray-200">
                                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                    {t('language.label')}
                                </div>
                                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                    {[
                                        { code: 'en', name: 'English', flag: '#flag-en-gb' },
                                        { code: 'es', name: 'Español', flag: '#flag-es' },
                                        { code: 'fr', name: 'Français', flag: '#flag-fr' }
                                    ].map((lang) => (
                                        <button
                                            key={lang.code}
                                            onClick={() => handleLanguageChange(lang.code)}
                                            className={`flex flex-col items-center gap-1 p-1.5 rounded-md transition-all min-w-[60px] ${
                                                currentLanguage === lang.code 
                                                    ? 'bg-blue-50 ring-2 ring-blue-500' 
                                                    : 'bg-gray-50 hover:bg-gray-100'
                                            }`}
                                            aria-label={`Switch to ${lang.name}`}
                                        >
                                            <svg className="h-5 w-5" aria-hidden="true">
                                                <use href={lang.flag} xlinkHref={lang.flag} />
                                            </svg>
                                            <span className={`text-[9px] font-medium leading-tight ${
                                                currentLanguage === lang.code ? 'text-blue-700' : 'text-gray-600'
                                            }`}>
                                                {lang.name}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
};

export default SiteHeader;

